import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface MatrixProps {
  head: string[];
  rows: string[][];
  /** Row indices that carry the argument. */
  accentRows?: number[];
  /** Column indices right-aligned with tabular figures. */
  numeric?: number[];
  minWidth?: string;
  className?: string;
  renderCell?: (cell: string) => ReactNode;
}

/** Dense comparison table. Scrolls inside its own container so the page never does. */
export function Matrix({
  head,
  rows,
  accentRows = [],
  numeric = [],
  minWidth = '560px',
  className,
  renderCell = (cell) => cell,
}: MatrixProps) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full border-collapse text-left" style={{ minWidth }}>
        <thead>
          <tr className="border-b border-ink">
            {head.map((label, i) => (
              <th key={label + i} className={cn('label py-2.5 pr-4 normal-case', numeric.includes(i) && 'pr-0 text-right')}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => {
            const accent = accentRows.includes(r);
            return (
              <tr key={r} className={cn('border-b border-line last:border-b-0', accent && 'bg-accent-soft/50')}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    className={cn(
                      'py-2.5 pr-4 align-top text-[13px] leading-relaxed',
                      c === 0 ? 'font-medium text-ink' : 'text-ink-2',
                      accent && c === 0 && 'text-accent-ink',
                      numeric.includes(c) && 'pr-0 text-right font-mono tabular'
                    )}
                  >
                    {renderCell(cell)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
